import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, BellOff, CheckCheck, ExternalLink } from "lucide-react";
import { useNotifications, useMarkNotificationRead } from "#/features/notifications/hooks";
import type { NotificationItem } from "#/features/notifications/types";

function timeAgo(dateStr?: string | null): string {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function isMembershipNotification(n: NotificationItem): boolean {
  return n.referenceType === "MEMBER_COMPANY";
}

export function MembershipNotificationsPanel({
  compact = false,
}: {
  compact?: boolean;
}) {
  const [page] = useState(0);
  const [filter, setFilter] = useState<"all" | "unread">("unread");
  const navigate = useNavigate();

  const { data, isLoading } = useNotifications(page, 50);
  const markRead = useMarkNotificationRead();

  const allItems = (data?.content ?? []).filter(isMembershipNotification);
  const unreadItems = allItems.filter((n) => !n.readAt);
  const visible = filter === "unread" ? unreadItems : allItems;
  const unreadCount = unreadItems.length;

  const handleMarkRead = (id: number) => markRead.mutate(id);

  const handleMarkAllRead = () => {
    unreadItems.forEach((n) => markRead.mutate(n.id));
  };

  const handleAction = (n: NotificationItem) => {
    if (n.referenceType === "MEMBER_COMPANY" && n.referenceId != null) {
      navigate(`/hr/membership/members/${n.referenceId}`);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 p-4 text-sm text-slate-400">
        <Bell className="h-4 w-4 animate-pulse" />
        Loading notifications…
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Bell className="h-4 w-4 text-slate-500" />
          <span className="text-sm font-black text-slate-700">Notifications</span>
          {unreadCount > 0 && (
            <span className="rounded-full bg-red-500 px-2 py-0.5 text-xs font-black text-white">
              {unreadCount}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setFilter(filter === "unread" ? "all" : "unread")}
            className="rounded-xl border border-slate-200 px-3 py-1 text-xs font-bold text-slate-600 hover:bg-slate-100"
          >
            {filter === "unread" ? "Show all" : "Unread only"}
          </button>

          {unreadCount > 0 && (
            <button
              type="button"
              onClick={handleMarkAllRead}
              className="flex items-center gap-1 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700 hover:bg-emerald-100"
            >
              <CheckCheck className="h-3 w-3" />
              Mark all read
            </button>
          )}
        </div>
      </div>

      {/* List */}
      {visible.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-slate-200 p-8 text-center">
          <BellOff className="h-8 w-8 text-slate-300" />
          <p className="text-sm font-bold text-slate-400">
            {filter === "unread" ? "No unread notifications" : "No notifications"}
          </p>
        </div>
      ) : (
        <div
          className={`grid gap-2 ${
            compact ? "" : "max-h-[520px] overflow-y-auto pr-1"
          }`}
        >
          {visible.map((n) => {
            const isRead = Boolean(n.readAt);
            return (
              <div
                key={n.id}
                className={`rounded-2xl border p-4 transition ${
                  isRead
                    ? "border-slate-200 bg-white"
                    : "border-blue-200 bg-blue-50"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      {!isRead && (
                        <span className="h-2 w-2 shrink-0 rounded-full bg-blue-500" />
                      )}
                      <p
                        className={`truncate text-sm font-black ${
                          isRead ? "text-slate-700" : "text-blue-900"
                        }`}
                      >
                        {n.title}
                      </p>
                    </div>

                    {n.message && (
                      <p className="mt-1 text-sm leading-snug text-slate-600">
                        {n.message}
                      </p>
                    )}
                  </div>

                  <span className="shrink-0 text-xs text-slate-400">
                    {timeAgo(n.createdAt)}
                  </span>
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {n.referenceId != null && (
                    <button
                      type="button"
                      onClick={() => handleAction(n)}
                      className="flex items-center gap-1 rounded-xl bg-emerald-700 px-3 py-1.5 text-xs font-black text-white hover:bg-emerald-800"
                    >
                      <ExternalLink className="h-3 w-3" />
                      View
                    </button>
                  )}

                  {!isRead && (
                    <button
                      type="button"
                      onClick={() => handleMarkRead(n.id)}
                      className="rounded-xl border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-100"
                    >
                      Mark as read
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
